import { IconButton, HStack, Stack, Text, useToast } from "@chakra-ui/react";
import { EditModalType } from "src/utils";
import { FaPlus } from "react-icons/fa";
import { EventDetailTicketHoldsSection } from "@common/interfaces/KYDEvent";
import ShowIfHasPermission from "src/Manager/ShowIfHasPermission";
import { SectionProps } from "../sections/sectionsComponents";
import TicketHoldAddDialog, {
  withAddTicketHoldDialog,
  TicketHoldAddDialogContext,
  TicketHoldFormData,
} from "./TicketHoldAddDialog";
import { useContext, useState } from "react";
import actions from "src/actions";
import TicketHoldsTable from "./TicketHoldsTable";
import dayjs from "dayjs";
import { KYDHold } from "@common/interfaces/KYDTicket";

interface Props {
  label: string;
  value: string;
  height?: string;
}

const TicketHoldsSection = ({
  isLoading,
  data,
  permissions,
  setModalType,
  refetchEvent,
}: SectionProps<EventDetailTicketHoldsSection["data"]>) => {
  const toast = useToast();
  const dialog = useContext(TicketHoldAddDialogContext);
  const [creating, setCreating] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editingHold, setEditingHold] = useState<KYDHold | null>(null);

  const handleSave = async (formData: TicketHoldFormData) => {
    setCreating(true);
    try {
      if (dialogMode === "edit" && editingHold) {
        await actions.ADMIN.updateHold(data.id, editingHold.id, {
          name: formData.label,
          quantity: formData.quantity
            ? parseInt(formData.quantity.toString())
            : undefined,
          ticket_type_id: formData.ticket_type_id,
          price: formData.price,
          expires_at: formData.expires_at,
        });
      } else {
        await actions.ADMIN.createHold(data.id, {
          name: formData.label,
          quantity: parseInt(formData.quantity.toString()),
          ticket_type_id: formData.ticket_type_id,
          price: formData.price,
          expires_at: formData.expires_at,
        });
      }
    } catch (err) {
      console.error("Failed to save hold", { err });
      toast({
        title:
          dialogMode === "edit"
            ? "Failed to update hold"
            : "Failed to create hold",
        description: "Please try again",
        status: "error",
      });
    }
    await refetchEvent(data.id);
    dialog.close();
    setCreating(false);
    setEditingHold(null);
    setDialogMode("create");
  };

  const onCreateHold = () => {
    setDialogMode("create");
    setEditingHold(null);
    dialog.open({ text: "" });
  };

  const onEditHold = (hold: any) => {
    setDialogMode("edit");
    setEditingHold({
      ...hold,
      custom_price: hold.price?.units?.toString() || hold.display?.price || "",
      label: hold.name,
      quantity: hold.quantity || hold.display?.quantity || 1,
      expires_at: hold.expires_at || "",
      ticket_type_id: hold.ticket_type_id,
      id: hold.id,
    });
    dialog.open({ text: "" });
  };

  const onReleaseHold = async (hold: any) => {
    try {
      await actions.ADMIN.releaseHold(data.id, hold.id);
      await refetchEvent(data.id);
      toast({
        title: "Hold released",
        status: "success",
      });
    } catch (err) {
      toast({
        title: "Failed to release hold",
        description: err?.message || "Please try again",
        status: "error",
      });
    }
  };

  return (
    <Stack
      direction={{ base: "column", md: "row" }}
      w="100%"
      alignItems={"center"}
    >
      <TicketHoldsTable
        permissions={permissions}
        eventLink={data.link}
        ticketHolds={data.ticket_holds}
        ticketTypePools={data.ticket_type_pools}
        accessCodes={data.access_codes}
        onCreateHold={onCreateHold}
        onEditHold={onEditHold}
        onReleaseHold={onReleaseHold}
      />
      <TicketHoldAddDialog
        ticketTypePools={data.ticket_type_pools}
        ticketTypes={data.ticket_types}
        timezone={data.timezone}
        isLoading={creating}
        onSave={handleSave}
        onClose={dialog.close}
        mode={dialogMode}
        hold={editingHold}
      />
    </Stack>
  );
};

export default withAddTicketHoldDialog(TicketHoldsSection);
